import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useDispatch, useSelector } from 'react-redux';
import { BaseTextInput, InputLabel } from 'components/forms';
import { PrimaryButton } from 'components/buttons';
import { TagPill } from 'components/badges';
import DeleteModal from 'components/layout/modals/DeleteModal/DeleteModal';
import { Skeleton } from 'components/layout/loading';
import Page from 'components/layout/Page/Page';
import {
  clearTodoTagError,
  createTodoTag,
  deleteTodoTag,
  fetchTodoTags,
  selectAllTodoTags,
  selectTodoTagsError,
  selectTodoTagsFetching,
  updateTodoTag,
} from 'store/slices/todoTagsSlice';
import { TAG_NAME_MAX_LENGTH, extractTagNamesFromInput, normalizeTagName } from 'constants/tags';
import TodoTabs from './components/TodoTabs/TodoTabs';

function getErrorMessage(error) {
  if (typeof error === 'string') {
    return error;
  }

  return error?.error || error?.message || 'Something went wrong.';
}

export default function TodoTags() {
  const dispatch = useDispatch();
  const todoTags = useSelector(selectAllTodoTags);
  const isFetching = useSelector(selectTodoTagsFetching);
  const error = useSelector(selectTodoTagsError);

  const [newTagName, setNewTagName] = useState('');
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [todoTagToDelete, setTodoTagToDelete] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [savingTagId, setSavingTagId] = useState(null);
  const [deletingTagName, setDeletingTagName] = useState(null);

  useEffect(() => {
    dispatch(fetchTodoTags());

    return () => {
      dispatch(clearTodoTagError());
    };
  }, [dispatch]);

  const sortedTodoTags = useMemo(
    () => [...todoTags].sort((left, right) => left.name.localeCompare(right.name)),
    [todoTags]
  );
  const activeEditingTag = sortedTodoTags.find((todoTag) => todoTag._id === editingTagId) || null;

  const isInitialLoading = isFetching && sortedTodoTags.length === 0;
  const isSavingEditedTag = activeEditingTag ? savingTagId === activeEditingTag._id : false;
  const hasValidEditingName = Boolean(normalizeTagName(editingName));

  const createTagsFromInput = async () => {
    const nextNames = extractTagNamesFromInput(newTagName);

    if (nextNames.length === 0) {
      return;
    }

    setIsCreating(true);
    try {
      for (const nextName of nextNames) {
        await dispatch(createTodoTag(nextName)).unwrap();
      }

      setNewTagName('');
    } catch {
      return;
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    await createTagsFromInput();
  };

  const handleCreateKeyDown = async (event) => {
    if (event.key !== 'Enter' && event.key !== ',') {
      return;
    }

    event.preventDefault();
    await createTagsFromInput();
  };

  const handleEditStart = (todoTag) => {
    setEditingTagId(todoTag._id);
    setEditingName(todoTag.name);
  };

  const handleEditCancel = () => {
    setEditingTagId(null);
    setEditingName('');
  };

  const handleDeleteStart = (todoTag) => {
    setTodoTagToDelete(todoTag);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleEditKeyDown = async (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleEditCancel();
      return;
    }

    if (event.key === 'Enter' && activeEditingTag) {
      event.preventDefault();
      await handleEditSave(activeEditingTag._id);
    }
  };

  const handleEditSave = async (tagId) => {
    const nextName = normalizeTagName(editingName);

    if (!nextName) {
      return;
    }

    setSavingTagId(tagId);
    try {
      await dispatch(updateTodoTag({ tagId, name: nextName })).unwrap();
      handleEditCancel();
    } catch {
      return;
    } finally {
      setSavingTagId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!todoTagToDelete) {
      return;
    }

    setDeletingTagName(todoTagToDelete.name);
    try {
      await dispatch(deleteTodoTag(todoTagToDelete.name)).unwrap();

      if (todoTagToDelete._id === editingTagId) {
        handleEditCancel();
      }

      setIsDeleteModalOpen(false);
    } catch {
      return;
    } finally {
      setDeletingTagName(null);
    }
  };

  const renderLoadingState = () => (
    <div className="px-6 py-5">
      <div className="space-y-3">
        <Skeleton variant="text" className="h-5 w-24" />
        <Skeleton className="h-10 w-full rounded-md" />
        <Skeleton className="h-10 w-28 rounded-md" />
      </div>
      <div className="mt-6 border-t border-white/10 pt-6">
        <div className="flex items-center justify-between gap-4">
          <Skeleton variant="text" className="h-4 w-24" />
          <Skeleton variant="text" className="h-4 w-16" />
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Skeleton key={item} className="h-10 w-28 rounded-full" />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <Page title="Tags" headerActions={<TodoTabs />}>
      <div className="mx-auto max-w-3xl space-y-6">
        <section className="overflow-hidden rounded-2xl border border-white/10 bg-gray-800/70 shadow-sm">
          <div className="border-b border-white/10 px-6 py-5">
            <h2 className="text-lg font-semibold text-white">Tag library</h2>
            <p className="mt-1 text-sm text-gray-400">
              Create reusable tags and attach them to your todos from the add or edit form.
            </p>
          </div>

          {isInitialLoading ? renderLoadingState() : (
            <>
              <div className="px-6 py-5">
                <form onSubmit={handleCreate} className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <InputLabel htmlFor="new-tag-name">New tag</InputLabel>
                    <div className="mt-2">
                      <BaseTextInput
                        id="new-tag-name"
                        value={newTagName}
                        onChange={(event) => setNewTagName(event.target.value)}
                        onKeyDown={handleCreateKeyDown}
                        maxLength={TAG_NAME_MAX_LENGTH}
                        placeholder="Customer, Finance, Personal"
                        disabled={isCreating}
                      />
                    </div>
                  </div>
                  <PrimaryButton
                    type="submit"
                    saving={isCreating}
                    disabled={extractTagNamesFromInput(newTagName).length === 0}
                  >
                    Add tag
                  </PrimaryButton>
                </form>
                <p className="mt-2 text-xs text-gray-500">
                  Separate multiple names with commas or press Enter to add them together.
                </p>

                {error && (
                  <div role="alert" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                    {getErrorMessage(error)}
                  </div>
                )}

                {activeEditingTag && (
                  <div className="mt-3 rounded-xl border border-indigo-500/20 bg-indigo-500/10 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-white">Rename tag</p>
                      <button
                        type="button"
                        aria-label="Close rename tag"
                        disabled={isSavingEditedTag}
                        onClick={handleEditCancel}
                        className="-mr-1 -mt-1 inline-flex size-7 items-center justify-center rounded-full text-gray-400 transition hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
                      >
                        <XMarkIcon className="size-4" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                      <BaseTextInput
                        value={editingName}
                        onChange={(event) => setEditingName(event.target.value)}
                        onKeyDown={handleEditKeyDown}
                        maxLength={TAG_NAME_MAX_LENGTH}
                        disabled={isSavingEditedTag}
                        autoFocus
                        className="sm:max-w-xs"
                      />
                      <PrimaryButton
                        type="button"
                        saving={isSavingEditedTag}
                        disabled={!hasValidEditingName}
                        onClick={() => handleEditSave(activeEditingTag._id)}
                      >
                        Save changes
                      </PrimaryButton>
                    </div>
                  </div>
                )}
              </div>

              {sortedTodoTags.length === 0 ? (
                <div className="border-t border-white/10 px-6 py-8 text-sm text-gray-400">
                  No tags yet. Create your first tag above.
                </div>
              ) : (
                <div className="border-t border-white/10 px-6 py-5">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-white">Existing tags</h3>
                      <p className="mt-1 text-sm text-gray-400">
                        Click a tag to rename it. Use the close button to remove it.
                      </p>
                    </div>
                    <span className="text-xs uppercase tracking-wide text-gray-500">
                      {sortedTodoTags.length} total
                    </span>
                  </div>

                  <ul className="mt-4 flex flex-wrap gap-2">
                    {sortedTodoTags.map((todoTag) => {
                      const isEditing = editingTagId === todoTag._id;
                      const isDeletingTag = deletingTagName === todoTag.name;

                      return (
                        <li key={todoTag._id}>
                          <TagPill
                            onClick={() => handleEditStart(todoTag)}
                            onRemove={() => handleDeleteStart(todoTag)}
                            removeLabel={`Delete tag ${todoTag.name}`}
                            disabled={Boolean(deletingTagName)}
                            className={classNames(
                              'text-sm',
                              isEditing && 'bg-indigo-500/15 text-indigo-100 ring-indigo-400/30',
                              isDeletingTag && 'opacity-60'
                            )}
                          >
                            {todoTag.name}
                          </TagPill>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteClose}
        title="Delete Tag"
        message={`Are you sure you want to delete tag "${todoTagToDelete?.name}"? This will remove the tag from all todos that currently use it.`}
        onConfirm={handleDeleteConfirm}
        loading={Boolean(deletingTagName)}
      />
    </Page>
  );
}
